import {
  Activity, Eye, FileText, Heart, MessageCircle, Radar, TrendingUp, UserCheck, Users,
  type LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { cn } from '../../components/ui/utils';
import { useValueChanged } from '../lib/useValueChanged';

/**
 * Одна метрика — одна иконка и один цвет во всём приложении.
 * Раньше «Охват» был Eye сверху и Users снизу, а Eye означал сразу охват и просмотры.
 */
export const METRICS = {
  accounts: { icon: UserCheck, tint: 'bg-primary/10 text-primary' },
  followers: { icon: Users, tint: 'bg-primary/10 text-primary' },
  reach: { icon: Radar, tint: 'bg-primary/10 text-primary' },
  views: { icon: Eye, tint: 'bg-primary/10 text-primary' },
  interactions: { icon: Activity, tint: 'bg-primary/10 text-primary' },
  likes: { icon: Heart, tint: 'bg-primary/10 text-primary' },
  comments: { icon: MessageCircle, tint: 'bg-primary/10 text-primary' },
  publications: { icon: FileText, tint: 'bg-primary/10 text-primary' },
  er: { icon: TrendingUp, tint: 'bg-primary/10 text-primary' },
} as const satisfies Record<string, { icon: LucideIcon; tint: string }>;

export interface StatCardProps {
  title: string;
  /** Короткое значение для показа: 235.9M */
  value: string;
  /** Точное значение в title при наведении: 235 919 030 */
  full?: string;
  subtitle?: string;
  icon: LucideIcon;
  /** Тон плашки под иконку: 'bg-primary/10 text-primary' */
  tint: string;
  loading?: boolean;
}

export function StatCard({ title, value, full, subtitle, icon: Icon, tint, loading }: StatCardProps) {
  // Цифры обновляются в фоне. Та, что изменилась, коротко подсвечивается —
  // иначе замена в один кадр проходит незамеченной.
  const justChanged = useValueChanged(value);

  return (
    <Card className="gap-0 transition-shadow hover:shadow-md">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tint}`}>
          <Icon className="h-4 w-4" />
        </span>
      </CardHeader>
      <CardContent className="pb-6">
        {loading ? (
          <div className="h-8 w-28 animate-pulse rounded bg-muted" />
        ) : (
          <div
            className={cn(
              'text-3xl font-bold tracking-tight tabular-nums transition-colors duration-700 motion-reduce:transition-none',
              justChanged && 'text-primary duration-150',
            )}
            title={full}
          >
            {value}
          </div>
        )}
        {subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}
